/**
 * Adapta una parcela CAD al shape de BrandedListingContent.
 * No toca brandedListingItems (listas catastrales / UF): el lote CAD no es un lot_id.
 */

import { formatListingPrice } from '../../Map/utils/listingCoreConstants';
import {
  formatCadArea,
  getCadParcelCoverUrl,
  getCadParcelId,
  getCadParcelListCardCopy,
  getCadParcelPhotos,
  getCadStockLabel,
  getCadUseLabel,
} from './cadParcelDisplay';
import { cadFeatureToPlanimetriaBoundary } from './cadGeometry';

const firstNonEmpty = (...values) => {
  const found = values.find((value) => {
    if (value == null) return false;
    return !(typeof value === 'string' && value.trim() === '');
  });
  return found === undefined ? null : found;
};

/**
 * Precio destacado. 0 o vacío = sin precio (no se muestra el bloque de precio).
 * @param {object|null|undefined} props
 * @returns {{ kind: 'price', amount: string, caption: string, source: 'cad' }|null}
 */
export function resolveCadListingHero(props) {
  const amount = Number(props?.price);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const priceLabel = formatListingPrice(amount, props?.currency || 'USD');
  if (!priceLabel) return null;
  return {
    kind: 'price',
    amount: priceLabel,
    caption: 'Precio',
    source: 'cad',
  };
}

/**
 * Metas comerciales del lote CAD (superficie, uso, estado). FOS/FOT y
 * orientación siguen en el readout de ficha, que es estructura CAD.
 * @param {object|null|undefined} props
 */
export function buildCadListingMetas(props) {
  const block = String(props?.block || '').trim();
  const area = formatCadArea(props?.area_m2);
  const useLabel = getCadUseLabel(props?.use);
  const stockLabel = getCadStockLabel(props?.stock_status || 'available');
  return [
    {
      key: 'block',
      label: 'Manzana',
      shortLabel: 'Mz.',
      value: block || null,
    },
    {
      key: 'surface',
      label: 'Superficie',
      shortLabel: 'Sup.',
      value: area !== '—' ? area : null,
    },
    {
      key: 'use',
      label: 'Uso',
      value: useLabel !== '—' ? useLabel : null,
    },
    { key: 'stock', label: 'Estado', value: stockLabel || null },
  ].filter((meta) => Boolean(meta.value));
}

/**
 * Presentación marketplace de una parcela CAD para la vista branded del visitante.
 * @param {object|null|undefined} feature
 * @returns {{ title: string, description: string|null, hero: object|null, metas: object[], images: object[] }|null}
 */
export function buildCadBrandedPresentation(feature) {
  if (!feature) return null;
  const props = feature.properties || {};
  const { primary } = getCadParcelListCardCopy(feature);
  const description = firstNonEmpty(props.description);
  return {
    title: primary,
    description: description ? String(description).trim() : null,
    hero: resolveCadListingHero(props),
    metas: buildCadListingMetas(props),
    images: getCadParcelPhotos(feature),
  };
}

/**
 * Item para `BrandedListingCard` en la lista visitante CAD (`/#/lista/:id`).
 * @param {object|null|undefined} feature
 * @param {{ projectName?: string }} [opts]
 */
export function buildCadListingItem(feature, { projectName } = {}) {
  const presentation = buildCadBrandedPresentation(feature);
  const lotId = getCadParcelId(feature);
  if (!presentation || !lotId) return null;
  const props = feature.properties || {};
  const { primary } = getCadParcelListCardCopy(feature);
  const identifier = firstNonEmpty(props.name, primary) || '—';
  return {
    key: `cad:${lotId}`,
    lotId,
    feature,
    title: presentation.title,
    hero: presentation.hero,
    metas: presentation.metas,
    coverImage: getCadParcelCoverUrl(feature),
    locationLabel: firstNonEmpty(projectName) || '—',
    cadastralLabel: identifier,
    copyIdLabel: 'Copiar identificador del lote',
    hasLotContent: false,
    boundary: cadFeatureToPlanimetriaBoundary(feature),
  };
}
